import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { MarketingLayout } from "../components/marketing/MarketingLayout";
import { SEO } from "../components/seo/SEO";
import { publicFormsApi } from "../lib/api";
import { FieldControl, FieldDisplayBlock, DISPLAY_ONLY_TYPES, headerStyle, fieldVisible } from "../components/marketing/formFields";

// Fixed id of the marketing site's own Contact Us form, seeded once into the
// platform workspace (see backend services/platform_crm.py) and managed via
// Platform Console → CRM → Leads → Forms.
const CONTACT_FORM_ID = "form-platform-contact";

export default function PublicContact() {
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const f = await publicFormsApi.get(CONTACT_FORM_ID);
        setForm(f);
      } catch (e) {
        setError("The contact form is unavailable right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const accent = form?.accentColor || "#2563EB";
  const onChange = (id, val) => setValues((v) => ({ ...v, [id]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await publicFormsApi.submit(CONTACT_FORM_ID, values);
      setDone(res.message || "Thanks! We've received your message.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout breadcrumb={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}>
      <SEO
        title="Contact Us | ServiceGuru Pro"
        description="Get in touch with the ServiceGuru Pro team — questions about pricing, a demo, or support."
        path="/contact"
      />

      <div className="max-w-5xl mx-auto px-5 lg:px-8 pt-14 pb-24 grid md:grid-cols-2 gap-12 items-start" data-testid="public-contact-page">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full mb-4">
            <Mail className="h-3.5 w-3.5" /> Contact us
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Let&apos;s talk about your business</h1>
          <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-md">
            Questions about pricing, a live demo, or how ServiceGuru Pro fits your trade? Send us a message and a real
            person will get back to you shortly.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-testid="public-contact-form-card">
          {loading ? (
            <div className="py-20 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
          ) : error && !form ? (
            <div className="py-16 px-6 text-center text-slate-500" data-testid="public-contact-error">{error}</div>
          ) : (
            <>
              {(() => { const hs = headerStyle(form.theme, accent); const onAccent = form.theme === "bold"; return (
                <div style={hs.wrap} className="px-6 pt-6 pb-3">
                  {form.logoDataUrl ? (
                    <img src={form.logoDataUrl} alt={form.companyName} className="h-10 mb-3 object-contain" />
                  ) : null}
                  <h2 className={`text-[20px] font-bold ${hs.title}`}>{form.title}</h2>
                  {form.intro && <p className={`text-[13.5px] mt-1.5 leading-relaxed ${hs.intro}`}>{form.intro}</p>}
                </div>
              ); })()}

              {done ? (
                <div className="px-6 py-10 flex flex-col items-center text-center" data-testid="public-contact-success">
                  <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${accent}1a` }}>
                    <CheckCircle2 className="h-7 w-7" style={{ color: accent }} />
                  </div>
                  <p className="text-[14px] text-slate-700 max-w-xs">{done}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="px-6 pb-6 pt-4 space-y-4">
                  {form.fields.filter((f) => fieldVisible(f, values)).map((f) =>
                    DISPLAY_ONLY_TYPES.has(f.type) ? (
                      <div key={f.id}><FieldDisplayBlock field={f} accent={accent} /></div>
                    ) : (
                      <div key={f.id}>
                        {f.type !== "checkbox" && (
                          <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                            {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                        )}
                        <FieldControl field={f} value={values[f.id]} onChange={(v) => onChange(f.id, v)} accent={accent} inputTextColor={form.inputTextColor} />
                      </div>
                    )
                  )}
                  {error && <div className="text-[13px] text-red-600" data-testid="public-contact-form-error">{error}</div>}
                  <button type="submit" disabled={submitting} data-testid="public-contact-submit"
                    style={{ backgroundColor: accent }}
                    className="w-full h-11 rounded-lg text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {form.submitLabel}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </MarketingLayout>
  );
}
